/*
  File: src/pages/ProductResizing.jsx
  Purpose: Product image resizing management page
  Features: Upload images, convert to WebP, resize to <35KB, preserve transparency
*/
import React, { useState, useCallback } from 'react';
import Navbar from '../components/layout/Navbar';
import { apiClient } from '../auth/api';

function ProductResizing() {
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [dragActive, setDragActive] = useState(false);

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    }, []);

    const handleFileSelect = (file) => {
        if (!file.type.startsWith('image/')) {
            setError('Please select a valid image file');
            return;
        }
        
        setSelectedFile(file);
        setError(null);
        setResult(null);
    };

    const handleFileInputChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleFileSelect(e.target.files[0]);
        }
    };

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    };

    const handleResize = async () => {
        if (!selectedFile) {
            setError('Please select an image first');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Convert file to base64
            const base64Image = await convertToBase64(selectedFile);
            
            // Call the backend endpoint
            const response = await apiClient.post('/media/resize-image', {
                image: base64Image
            });

            setResult({
                originalFile: selectedFile,
                resizedImage: response.data.image,
                sizeKb: response.data.size_kb,
                dimensions: response.data.dimensions,
                originalSize: (selectedFile.size / 1024).toFixed(2)
            });
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to resize image');
        } finally {
            setLoading(false);
        }
    };

    const downloadResizedImage = () => {
        if (!result?.resizedImage) return;

        // Create download link
        const link = document.createElement('a');
        link.href = result.resizedImage;
        link.download = `resized_${selectedFile.name.replace(/\.[^/.]+$/, "")}.webp`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const reset = () => {
        setSelectedFile(null);
        setResult(null);
        setError(null);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#0a0e1a] via-[#1a1f2e] to-[#2a2f3e]">
            <Navbar />
            <main className="pt-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-white mb-4">
                            Product Image Resizing
                        </h1>
                        <p className="text-white/60 text-lg max-w-2xl mx-auto">
                            Upload product images to convert them to WebP format with transparency preservation 
                            and automatic size optimization (target: ≤50KB).
                        </p>
                    </div>

                    <div className="space-y-8">
                        {/* Upload Section */}
                        <div className="bg-gradient-to-br from-[#1a2448]/80 to-[#1a2448]/60 rounded-xl p-6 border border-[#4cc3e9]/30">
                            <h2 className="text-xl font-semibold text-white mb-4">Upload Image</h2>
                            
                            <div
                                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                                    dragActive
                                        ? 'border-[#4cc3e9] bg-[#4cc3e9]/10'
                                        : 'border-white/30 hover:border-[#4cc3e9]/50'
                                }`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                            >
                                {selectedFile ? (
                                    <div className="space-y-4">
                                        <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
                                            <span className="text-3xl">✓</span>
                                        </div>
                                        <div>
                                            <p className="text-white font-medium">{selectedFile.name}</p>
                                            <p className="text-white/60 text-sm">
                                                Size: {(selectedFile.size / 1024).toFixed(2)} KB
                                            </p>
                                        </div>
                                        <button
                                            onClick={reset}
                                            className="text-[#4cc3e9] hover:text-[#4cc3e9]/80 text-sm underline"
                                        >
                                            Choose different file
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="w-20 h-20 mx-auto rounded-full bg-white/10 flex items-center justify-center">
                                            <span className="text-3xl">📁</span>
                                        </div>
                                        <div>
                                            <p className="text-white font-medium mb-2">
                                                Drag & drop an image here, or click to select
                                            </p>
                                            <p className="text-white/60 text-sm">
                                                Supports JPG, PNG, GIF, and other image formats
                                            </p>
                                        </div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFileInputChange}
                                            className="hidden"
                                            id="file-upload"
                                        />
                                        <label
                                            htmlFor="file-upload"
                                            className="inline-block px-6 py-2 bg-[#4cc3e9] text-white rounded-lg hover:bg-[#4cc3e9]/80 transition-colors cursor-pointer"
                                        >
                                            Browse Files
                                        </label>
                                    </div>
                                )}
                            </div>

                            {error && (
                                <div className="mt-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
                                    <p className="text-red-300">{error}</p>
                                </div>
                            )}

                            {selectedFile && !result && (
                                <div className="mt-6 flex justify-center">
                                    <button
                                        onClick={handleResize}
                                        disabled={loading}
                                        className="px-8 py-3 bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] text-white rounded-lg hover:from-[#7b3ff2]/80 hover:to-[#9060ff]/80 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        {loading ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                🔄 Resize & Convert
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Results Section */}
                        {result && (
                            <div className="bg-gradient-to-br from-[#1a2448]/80 to-[#1a2448]/60 rounded-xl p-6 border border-[#4cc3e9]/30">
                                <h2 className="text-xl font-semibold text-white mb-6">Conversion Results</h2>
                                
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    {/* Original Image */}
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-medium text-white">Original Image</h3>
                                        <div className="bg-white/5 rounded-lg p-4">
                                            <img
                                                src={URL.createObjectURL(result.originalFile)}
                                                alt="Original"
                                                className="w-full h-48 object-contain rounded-lg bg-white/10"
                                            />
                                            <div className="mt-4 space-y-2 text-sm text-white/60">
                                                <p><span className="text-white">Format:</span> {result.originalFile.type}</p>
                                                <p><span className="text-white">Size:</span> {result.originalSize} KB</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Resized Image */}
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-medium text-white">Optimized Image</h3>
                                        <div className="bg-white/5 rounded-lg p-4">
                                            <img
                                                src={result.resizedImage}
                                                alt="Resized"
                                                className="w-full h-48 object-contain rounded-lg bg-white/10"
                                            />
                                            <div className="mt-4 space-y-2 text-sm text-white/60">
                                                <p><span className="text-white">Format:</span> WebP</p>
                                                <p><span className="text-white">Size:</span> {result.sizeKb} KB</p>
                                                <p><span className="text-white">Dimensions:</span> {result.dimensions.width} × {result.dimensions.height}</p>
                                                <p className="text-green-400">
                                                    <span className="text-white">Reduction:</span> {(100 - (result.sizeKb / result.originalSize) * 100).toFixed(1)}%
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 flex justify-center gap-4">
                                    <button
                                        onClick={downloadResizedImage}
                                        className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                                    >
                                        📥 Download WebP
                                    </button>
                                    <button
                                        onClick={reset}
                                        className="px-6 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                                    >
                                        Process Another Image
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}

export default ProductResizing;